import { api } from './api';

export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) output[index] = raw.charCodeAt(index);
  return output;
}

export async function registerPwa(onUpdate: (registration: ServiceWorkerRegistration) => void): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  if (registration.waiting) onUpdate(registration);
  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    installing?.addEventListener('statechange', () => {
      if (installing.state === 'installed' && navigator.serviceWorker.controller) onUpdate(registration);
    });
  });
  return registration;
}

export async function enablePush(registration: ServiceWorkerRegistration): Promise<PushSubscription> {
  if (!('PushManager' in window)) throw new Error('هذا المتصفح لا يدعم إشعارات الويب.');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new DOMException('Permission denied', 'NotAllowedError');
  const current = await registration.pushManager.getSubscription();
  if (current) return current;
  const { publicKey } = await api.pushPublicKey();
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey)
  });
  await api.createPushSubscription(subscription.toJSON());
  return subscription;
}

export async function disablePush(registration: ServiceWorkerRegistration): Promise<void> {
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  await api.removePushSubscription(subscription.endpoint);
  await subscription.unsubscribe();
}

export function isPushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

const CALL_ACTIVE_STATUSES: readonly string[] = ['reserved', 'connecting', 'active', 'reconnecting', 'ending'];

/** A PWA update must never force-reload the page while a call/recording is live. */
export function callBlocksUpdate(status: string): boolean {
  return CALL_ACTIVE_STATUSES.includes(status);
}

/**
 * Activates a waiting service worker. Callers must confirm no call/recording is
 * active first so the page is never force-reloaded during sensitive work.
 */
export function activateUpdate(registration: ServiceWorkerRegistration): void {
  const waiting = registration.waiting;
  if (!waiting) return;
  waiting.postMessage({ type: 'SKIP_WAITING' });
  waiting.addEventListener('statechange', () => {
    if (waiting.state === 'activated') window.location.reload();
  });
}
